'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { X, AlertCircle, CheckCircle, UserX, FileCheck, Plus } from 'lucide-react'
import {
  applyToJob,
  getMyVaultForAttach,
  type MyVaultForAttach,
  type VaultPromptItem,
  type VaultPackageItem,
} from '../../_actions/job-application-actions'
import {
  MAX_APPLICATION_ATTACHMENTS,
  type ApplicationAttachmentInput,
  type ApplicationAttachmentKind,
} from '@/lib/validations/job.schema'

interface ApplyModalProps {
  jobId: string
  jobTitle: string
  companyName: string
  onClose: () => void
  onSuccess: () => void
}

type SelectedAttachment = ApplicationAttachmentInput & { title: string }

export function ApplyModal({ jobId, jobTitle, companyName, onClose, onSuccess }: ApplyModalProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const [coverLetter, setCoverLetter] = useState('')
  const [attachments, setAttachments] = useState<SelectedAttachment[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Store previously focused element and focus the dialog on mount
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement
    dialogRef.current?.focus()
    return () => {
      previousFocusRef.current?.focus()
    }
  }, [])

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }, [])

  const removeAttachment = (kind: ApplicationAttachmentKind, id: string) => {
    setAttachments((prev) => prev.filter((a) => !(a.kind === kind && a.id === id)))
  }

  const addAttachments = (next: SelectedAttachment[]) => {
    setAttachments(next.slice(0, MAX_APPLICATION_ATTACHMENTS))
    setPickerOpen(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)
    setErrorCode(null)

    const result = await applyToJob({
      jobId,
      coverLetter: coverLetter || undefined,
      attachments: attachments.length
        ? attachments.map(({ kind, id }) => ({ kind, id }))
        : undefined,
    })

    setIsSubmitting(false)

    if (result.success) {
      setSuccess(true)
    } else {
      setError(result.error)
      setErrorCode(result.code ?? null)
    }
  }

  // Auto-close after success with cleanup
  useEffect(() => {
    if (!success) return
    const timer = setTimeout(() => {
      onSuccess()
      onClose()
    }, 1500)
    return () => clearTimeout(timer)
  }, [success, onSuccess, onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} role="presentation" />

      {/* Modal */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-lg rounded-[var(--radius-xl)] bg-[var(--color-bg-surface)] p-6 shadow-[var(--shadow-lg)]"
        role="dialog"
        aria-modal="true"
        aria-label={`Apply to ${jobTitle}`}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {success ? (
          <div className="flex flex-col items-center py-8 text-center">
            <CheckCircle className="h-12 w-12 text-[var(--color-success)]" />
            <h2
              className="mt-4 text-lg font-semibold text-[var(--color-text-primary)]"
              style={font}
            >
              Application submitted!
            </h2>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]" style={font}>
              {companyName} will review your profile and get back to you.
            </p>
          </div>
        ) : errorCode === 'FORBIDDEN' ? (
          /* Profile error: not individual or incomplete */
          <div className="flex flex-col items-center py-8 text-center">
            <UserX className="h-12 w-12 text-[var(--color-text-muted)]" />
            <h2
              className="mt-4 text-lg font-semibold text-[var(--color-text-primary)]"
              style={font}
            >
              Can&apos;t apply
            </h2>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]" style={font}>
              {error}
            </p>
            {error?.includes('complete your profile') && (
              <Link
                href="/settings/account"
                className="mt-4 inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-gradient-to-r from-[var(--color-brand-indigo)] to-[var(--color-brand-violet)] px-4 py-2 text-sm font-medium text-white hover:brightness-110"
                style={font}
                onClick={onClose}
              >
                Complete profile
              </Link>
            )}
            <button
              type="button"
              onClick={onClose}
              className="mt-3 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
              style={font}
            >
              Close
            </button>
          </div>
        ) : errorCode === 'CONFLICT' ? (
          /* Already applied */
          <div className="flex flex-col items-center py-8 text-center">
            <FileCheck className="h-12 w-12 text-[var(--color-brand-indigo)]" />
            <h2
              className="mt-4 text-lg font-semibold text-[var(--color-text-primary)]"
              style={font}
            >
              Already applied
            </h2>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]" style={font}>
              You&apos;ve already submitted an application for this role. You can track its status in the Applications tab on your profile.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-border-default)] px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
              style={font}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2
              className="text-lg font-semibold text-[var(--color-text-primary)]"
              style={font}
            >
              Apply to {jobTitle}
            </h2>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]" style={font}>
              at {companyName}
            </p>

            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 p-3">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-error)]" />
                <p className="text-sm text-[var(--color-error)]" style={font}>{error}</p>
              </div>
            )}

            <div className="mt-4">
              <label
                htmlFor="cover-letter"
                className="block text-sm font-medium text-[var(--color-text-primary)]"
                style={font}
              >
                Cover letter <span className="text-[var(--color-text-muted)]">(optional)</span>
              </label>
              <textarea
                id="cover-letter"
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder="Tell the company why you're a great fit for this role..."
                className="mt-1.5 w-full resize-none rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
                style={font}
              />
              <p className="mt-1 text-right text-xs text-[var(--color-text-muted)]" style={font}>
                {coverLetter.length}/2,000
              </p>
            </div>

            {/* Portfolio attachments */}
            <div className="mt-4">
              <label
                className="block text-sm font-medium text-[var(--color-text-primary)]"
                style={font}
              >
                Attach from your vault{' '}
                <span className="text-[var(--color-text-muted)]">
                  (optional, max {MAX_APPLICATION_ATTACHMENTS})
                </span>
              </label>

              {attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {attachments.map((att) => (
                    <span
                      key={`${att.kind}:${att.id}`}
                      className="inline-flex max-w-[220px] items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-2 py-1 text-xs text-[var(--color-text-primary)]"
                      style={font}
                    >
                      <span
                        className="rounded bg-[var(--color-brand-indigo)]/15 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--color-brand-indigo)]"
                      >
                        {att.kind}
                      </span>
                      <span className="truncate">{att.title}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(att.kind, att.id)}
                        className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                        aria-label={`Remove ${att.title}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                disabled={attachments.length >= MAX_APPLICATION_ATTACHMENTS}
                className="mt-2 inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-dashed border-[var(--color-border-default)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
                style={font}
                aria-label="Attach portfolio items"
              >
                <Plus className="h-3.5 w-3.5" />
                {attachments.length === 0
                  ? 'Add prompt or package'
                  : `Add more (${attachments.length}/${MAX_APPLICATION_ATTACHMENTS})`}
              </button>
            </div>

            <p className="mt-3 text-xs text-[var(--color-text-muted)]" style={font}>
              Your public profile will be shared with the company. Make sure it&apos;s up to date.
            </p>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-[var(--radius-md)] border border-[var(--color-border-default)] px-4 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                style={font}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 rounded-[var(--radius-md)] bg-gradient-to-r from-[var(--color-brand-indigo)] to-[var(--color-brand-violet)] px-4 py-2.5 text-sm font-medium text-white hover:brightness-110 disabled:opacity-50"
                style={font}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        )}

        {pickerOpen && (
          <VaultPicker
            initialSelected={attachments}
            onCancel={() => setPickerOpen(false)}
            onConfirm={addAttachments}
          />
        )}
      </div>
    </div>
  )
}

// ─── Vault picker dialog ─────────────────────────────────────────────

interface VaultPickerProps {
  initialSelected: SelectedAttachment[]
  onCancel: () => void
  onConfirm: (selected: SelectedAttachment[]) => void
}

function VaultPicker({ initialSelected, onCancel, onConfirm }: VaultPickerProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const [tab, setTab] = useState<ApplicationAttachmentKind>('prompt')
  const [vault, setVault] = useState<MyVaultForAttach | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selected, setSelected] = useState<SelectedAttachment[]>(initialSelected)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const result = await getMyVaultForAttach()
      if (cancelled) return
      if (result.success && result.data) {
        setVault(result.data)
      } else if (!result.success) {
        setLoadError(result.error)
      }
      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const isSelected = (kind: ApplicationAttachmentKind, id: string) =>
    selected.some((s) => s.kind === kind && s.id === id)

  const toggle = (kind: ApplicationAttachmentKind, id: string, title: string) => {
    setSelected((prev) => {
      if (prev.some((s) => s.kind === kind && s.id === id)) {
        return prev.filter((s) => !(s.kind === kind && s.id === id))
      }
      if (prev.length >= MAX_APPLICATION_ATTACHMENTS) return prev
      return [...prev, { kind, id, title }]
    })
  }

  const renderPrompts = (items: VaultPromptItem[]) => {
    if (items.length === 0) {
      return (
        <p className="px-3 py-6 text-center text-xs text-[var(--color-text-muted)]" style={font}>
          You don&apos;t have any prompts yet.
        </p>
      )
    }
    return items.map((p) => {
      const checked = isSelected('prompt', p.id)
      const disabled = !checked && selected.length >= MAX_APPLICATION_ATTACHMENTS
      return (
        <button
          key={p.id}
          type="button"
          onClick={() => toggle('prompt', p.id, p.title)}
          disabled={disabled}
          className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors ${
            checked
              ? 'bg-[var(--color-brand-indigo)]/10'
              : 'hover:bg-[var(--color-bg-elevated)]'
          } ${disabled ? 'opacity-50' : ''}`}
          style={font}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-[var(--color-text-primary)]">
              {p.title}
            </span>
            <span className="block text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
              {p.visibility}
            </span>
          </span>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => undefined}
            tabIndex={-1}
            className="h-4 w-4 flex-shrink-0 accent-[var(--color-brand-indigo)]"
            aria-hidden="true"
          />
        </button>
      )
    })
  }

  const renderPackages = (items: VaultPackageItem[]) => {
    if (items.length === 0) {
      return (
        <p className="px-3 py-6 text-center text-xs text-[var(--color-text-muted)]" style={font}>
          You don&apos;t have any packages yet.
        </p>
      )
    }
    return items.map((p) => {
      const checked = isSelected('package', p.id)
      const disabled = !checked && selected.length >= MAX_APPLICATION_ATTACHMENTS
      return (
        <button
          key={p.id}
          type="button"
          onClick={() => toggle('package', p.id, p.title)}
          disabled={disabled}
          className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors ${
            checked
              ? 'bg-[var(--color-brand-indigo)]/10'
              : 'hover:bg-[var(--color-bg-elevated)]'
          } ${disabled ? 'opacity-50' : ''}`}
          style={font}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-[var(--color-text-primary)]">
              {p.title}
            </span>
            <span className="block text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
              {p.status}
            </span>
          </span>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => undefined}
            tabIndex={-1}
            className="h-4 w-4 flex-shrink-0 accent-[var(--color-brand-indigo)]"
            aria-hidden="true"
          />
        </button>
      )
    })
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} role="presentation" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Pick portfolio attachments"
        className="relative flex max-h-[80vh] w-full max-w-md flex-col rounded-[var(--radius-xl)] bg-[var(--color-bg-surface)] shadow-[var(--shadow-lg)]"
      >
        <div className="flex items-center justify-between border-b border-[var(--color-border-default)] px-4 py-3">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]" style={font}>
            Attach from your vault
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            aria-label="Close picker"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex border-b border-[var(--color-border-default)]" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'prompt'}
            onClick={() => setTab('prompt')}
            className={`flex-1 px-3 py-2 text-xs font-medium ${
              tab === 'prompt'
                ? 'border-b-2 border-[var(--color-brand-indigo)] text-[var(--color-text-primary)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
            }`}
            style={font}
          >
            Prompts {vault ? `(${vault.prompts.length})` : ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'package'}
            onClick={() => setTab('package')}
            className={`flex-1 px-3 py-2 text-xs font-medium ${
              tab === 'package'
                ? 'border-b-2 border-[var(--color-brand-indigo)] text-[var(--color-text-primary)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
            }`}
            style={font}
          >
            Packages {vault ? `(${vault.packages.length})` : ''}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="px-3 py-6 text-center text-xs text-[var(--color-text-muted)]" style={font}>
              Loading your vault...
            </p>
          ) : loadError ? (
            <p className="px-3 py-6 text-center text-xs text-[var(--color-error)]" style={font}>
              {loadError}
            </p>
          ) : vault ? (
            <div className="divide-y divide-[var(--color-border-default)]">
              {tab === 'prompt' ? renderPrompts(vault.prompts) : renderPackages(vault.packages)}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[var(--color-border-default)] px-4 py-3">
          <span className="text-xs text-[var(--color-text-muted)]" style={font}>
            {selected.length}/{MAX_APPLICATION_ATTACHMENTS} selected
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
              style={font}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(selected)}
              className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-3 py-1.5 text-xs font-medium text-white hover:brightness-110"
              style={font}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
